class CodeltyHeroSlideshow extends HTMLElement {
  constructor() {
    super();
    this.slides = [];
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.userPausedAutoplay = false;
    this.interactionPauseCount = 0;
    this.isTransitioning = false;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-codelty-slide]'));
    if (this.slides.length === 0) return;

    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
    this.loopEnabled = this.dataset.loop !== 'false';
    this.swipeEnabled = this.dataset.swipe !== 'false';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.prevButton = this.querySelector('[name="previous"]');
    this.nextButton = this.querySelector('[name="next"]');
    this.dots = Array.from(this.querySelectorAll('.codelty-hero__dot'));
    this.autoplayButton = this.querySelector('.codelty-hero__autoplay');

    this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1, true));
    this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1, true));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index, 10);
        if (!Number.isNaN(index)) this.goTo(index, true);
      });
    });
    this.autoplayButton?.addEventListener('click', () => this.toggleAutoplay());

    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('mouseenter', () => this.pauseForInteraction());
      this.addEventListener('mouseleave', () => this.resumeFromInteraction());
    }

    if (this.swipeEnabled && this.slides.length > 1) {
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
    }

    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.playSlideMedia(this.slides[this.currentIndex]);
    this.startAutoplayIfAllowed();
  }

  disconnectedCallback() {
    this.clearAutoplay();
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.removeEventListener('touchstart', this.onTouchStart);
    this.removeEventListener('touchend', this.onTouchEnd);
  }

  onTouchStart(event) {
    if (event.touches.length !== 1) return;
    this.touchOnLink = !!event.target.closest('a');
    if (this.touchOnLink) return;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
  }

  onTouchEnd(event) {
    if (this.touchOnLink) {
      this.touchOnLink = false;
      return;
    }
    if (event.changedTouches.length !== 1) return;
    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    const deltaY = event.changedTouches[0].clientY - this.touchStartY;
    if (Math.abs(deltaX) < 40 || Math.abs(deltaY) > Math.abs(deltaX)) return;

    if (deltaX < 0) {
      this.goTo(this.currentIndex + 1, true);
    } else {
      this.goTo(this.currentIndex - 1, true);
    }
  }

  onBlockSelect(event) {
    const slide = event.target.closest('[data-codelty-slide]');
    if (!slide || !this.contains(slide)) return;

    this.pauseForInteraction();
    const index = parseInt(slide.dataset.slideIndex, 10);
    if (!Number.isNaN(index)) this.goTo(index, false);
  }

  onBlockDeselect(event) {
    const slide = event.target.closest('[data-codelty-slide]');
    if (!slide || !this.contains(slide)) return;
    this.resumeFromInteraction();
  }

  resolveIndex(index) {
    const total = this.slides.length;
    if (total <= 1) return 0;
    if (this.loopEnabled) {
      return ((index % total) + total) % total;
    }
    return Math.max(0, Math.min(index, total - 1));
  }

  goTo(index, userTriggered) {
    if (this.isTransitioning) return;

    const total = this.slides.length;
    if (total <= 1) return;

    const nextIndex = this.resolveIndex(index);
    if (nextIndex === this.currentIndex) return;

    const outgoing = this.slides[this.currentIndex];
    const incoming = this.slides[nextIndex];

    this.isTransitioning = true;
    outgoing.classList.add('codelty-hero__slide--exiting');
    outgoing.classList.remove('codelty-hero__slide--active');
    outgoing.setAttribute('aria-hidden', 'true');
    this.pauseSlideMedia(outgoing);

    incoming.classList.add('codelty-hero__slide--entering', 'codelty-hero__slide--active');
    incoming.setAttribute('aria-hidden', 'false');
    this.playSlideMedia(incoming);

    window.setTimeout(() => {
      outgoing.classList.remove('codelty-hero__slide--exiting');
      incoming.classList.remove('codelty-hero__slide--entering');
      this.isTransitioning = false;
    }, this.reducedMotion.matches ? 0 : 850);

    this.currentIndex = nextIndex;

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.currentIndex;
      dot.classList.toggle('codelty-hero__dot--active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (userTriggered && this.autoplayEnabled) {
      this.clearAutoplay();
      this.startAutoplayIfAllowed();
    }
  }

  pauseSlideMedia(slide) {
    if (!slide) return;
    slide.querySelectorAll('video').forEach((video) => video.pause());
  }

  playSlideMedia(slide) {
    if (!slide) return;
    slide.querySelectorAll('video').forEach((video) => {
      video.play().catch(() => {});
    });
  }

  pauseForInteraction() {
    this.interactionPauseCount += 1;
    this.clearAutoplay();
  }

  resumeFromInteraction() {
    this.interactionPauseCount = Math.max(0, this.interactionPauseCount - 1);
    this.startAutoplayIfAllowed();
  }

  startAutoplayIfAllowed() {
    if (!this.autoplayEnabled || this.slides.length < 2 || this.reducedMotion.matches) return;
    if (this.userPausedAutoplay || this.interactionPauseCount > 0) return;
    this.play();
  }

  play() {
    if (!this.autoplayEnabled || this.slides.length < 2 || this.reducedMotion.matches) return;
    if (this.userPausedAutoplay || this.interactionPauseCount > 0) return;

    this.clearAutoplay();
    this.autoplayTimer = window.setInterval(() => {
      this.goTo(this.currentIndex + 1, false);
    }, this.autoplaySpeed);
    this.autoplayButton?.classList.remove('codelty-hero__autoplay--paused');
  }

  toggleAutoplay() {
    this.userPausedAutoplay = !this.userPausedAutoplay;

    if (this.userPausedAutoplay) {
      this.clearAutoplay();
      this.autoplayButton?.classList.add('codelty-hero__autoplay--paused');
      return;
    }

    this.startAutoplayIfAllowed();
  }

  clearAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

customElements.define('codelty-hero-slideshow', CodeltyHeroSlideshow);
